import React, { useEffect, useRef, useState } from 'react';


// Inject styles once per page, no matter how many instances mount.
let styleInjected = false;
function injectStyle() {
    if (styleInjected || typeof document === 'undefined') return;
    const el = document.createElement('style');
    el.setAttribute('data-cmn-multiselect', '');
    el.textContent = MS_CSS;
    document.head.appendChild(el);
    styleInjected = true;
}

function CommonMultiSelect({ value = [], onChange, options = [], placeholder = 'Select…', disabled }) {
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => { injectStyle(); }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const selected = Array.isArray(value) ? value : [];
    const toggle = (val) => {
        onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
    };
    const labelFor = (val) => {
        const found = options.find(o => o.value === val);
        return found ? found.label : val;
    };

    return (
        <div className={`cmn-ms${disabled ? ' cmn-ms--disabled' : ''}`} ref={boxRef}>
            <div className="cmn-ms-control" onClick={() => !disabled && setOpen(o => !o)}>
                {selected.length === 0 ? (
                    <span className="cmn-ms-placeholder">{placeholder}</span>
                ) : (
                    <div className="cmn-ms-chips">
                        {selected.map(val => (
                            <span key={val} className="cmn-ms-chip">
                                {labelFor(val)}
                                <span className="cmn-ms-chip-x"
                                    onClick={(e) => { e.stopPropagation(); toggle(val); }}>×</span>
                            </span>
                        ))}
                    </div>
                )}
                <span className={`cmn-ms-caret${open ? ' open' : ''}`}>▾</span>
            </div>
            {open && (
                <div className="cmn-ms-menu">
                    {options.length === 0 && <div className="cmn-ms-empty">No options</div>}
                    {options.map(o => (
                        <label key={o.value} className="cmn-ms-opt">
                            <input type="checkbox" checked={selected.includes(o.value)}
                                onChange={() => toggle(o.value)} />
                            <span>{o.label}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

const MS_CSS = `
.cmn-ms { position: relative; width: 100%; font-family: 'Outfit', sans-serif; }
.cmn-ms-control {
    display: flex; align-items: center; gap: 8px; min-height: 40px;
    background: #0b1220; border: 1px solid #334155; border-radius: 8px;
    padding: 6px 10px; cursor: pointer; transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.cmn-ms-control:hover { border-color: #475569; }
.cmn-ms--disabled .cmn-ms-control { opacity: 0.6; cursor: not-allowed; }
.cmn-ms-placeholder { color: #475569; font-size: 13px; flex: 1; }
.cmn-ms-chips { display: flex; flex-wrap: wrap; gap: 6px; flex: 1; }
.cmn-ms-chip {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(59,130,246,0.16); color: #bfdbfe;
    border: 1px solid rgba(59,130,246,0.4); border-radius: 6px;
    padding: 2px 8px; font-size: 12px; font-weight: 500;
}
.cmn-ms-chip-x { cursor: pointer; color: #93c5fd; font-size: 14px; line-height: 1; }
.cmn-ms-chip-x:hover { color: #fff; }
.cmn-ms-caret { color: #64748b; font-size: 12px; transition: transform 0.15s ease; }
.cmn-ms-caret.open { transform: rotate(180deg); }

/* In-flow (not absolute) so it can't be clipped by the modal body's
   overflow — it expands the field and the modal body scrolls to it. */
.cmn-ms-menu {
    margin-top: 4px;
    background: #0f172a; border: 1px solid #334155; border-radius: 8px;
    box-shadow: 0 12px 32px -8px rgba(0,0,0,0.6);
    max-height: 220px; overflow-y: auto; padding: 6px;
}
.cmn-ms-empty { color: #64748b; font-size: 12.5px; padding: 8px 10px; }
.cmn-ms-opt {
    display: flex; align-items: center; gap: 9px; cursor: pointer;
    color: #cbd5e1; font-size: 13px; padding: 7px 10px; border-radius: 6px;
    user-select: none;
}
.cmn-ms-opt:hover { background: #1e293b; }
.cmn-ms-opt input { width: 15px; height: 15px; cursor: pointer; accent-color: #3b82f6; }
`;

export default CommonMultiSelect;
